const PrivacyPolicy = () => {
  return (
    <div className="fixed inset-0 z-50 bg-neutral-100 overflow-y-auto">
      <div className="min-h-screen flex justify-center px-4 py-16">
        <div className="w-full max-w-5xl bg-white rounded-lg border border-neutral-200 px-8 sm:px-12 py-12">
          
          {/* Title */}
          <h1 className="text-4xl font-semibold text-neutral-900 text-center mb-12">
            Privacy Policy
          </h1>

          {/* Content */}
          <div className="prose prose-neutral max-w-none text-neutral-800">
            <p className="text-base leading-relaxed">
              At <strong>Busiconn</strong> (operated by Mansud Enterprises Pvt.
              Ltd.), your privacy matters. This Privacy Policy explains what
              personal information we collect, how we use it, and your rights.
              Busiconn enables users to create profiles and posts, communicate,
              follow other users, and complete protected transactions.
            </p>

            {/* SECTION */}
            <section className="mt-12">
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Information We Collect
              </h2>

              <div className="border border-neutral-200 rounded-md p-6">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account and profile data such as name, email address, phone number, profile image, biography, and authentication provider identifiers.</li>
                  <li>Identity, KYC, payout, and transaction data such as PAN, bank details, delivery addresses, payment status, refunds, and dispute records.</li>
                  <li>User content and communications such as posts, comments, images, videos, messages, reports, blocks, ratings, and moderation records.</li>
                  <li>Device and app data needed for operation, such as push-notification tokens, app version, device platform, and security or diagnostic events.</li>
                  <li>Usage data such as follows, likes, saves, trust score activity, and feature interactions.</li>
                </ul>
              </div>

              <div className="mt-6 border border-neutral-200 rounded-md p-6 bg-neutral-50">
                <p className="font-medium mb-2">
                  Content & Communication Monitoring
                </p>
                <p className="text-sm leading-relaxed">
                  Busiconn may review conversations, content uploads, reports,
                  and transaction activity to prevent fraud, enforce platform
                  rules, moderate user-generated content, and resolve disputes.
                </p>
              </div>
            </section>

            {/* SECTION */}
            <section className="mt-12">
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                How We Use Your Information
              </h2>

              <div className="border border-neutral-200 rounded-md p-6">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Create and secure accounts, including phone, Google, and Apple sign-in</li>
                  <li>Operate profiles, posts, comments, chats, follows, notifications, and moderation</li>
                  <li>Process protected payments, KYC, payouts, cancellations, refunds, and disputes</li>
                  <li>Detect fraud, enforce our Terms, comply with law, and improve reliability</li>
                </ul>
              </div>
            </section>

            {/* SECTION */}
            <section className="mt-12">
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Data Sharing
              </h2>

              <div className="border border-neutral-200 rounded-md p-6">
                <p className="mb-4">
                  We do <strong>not sell</strong> your personal or earnings data.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Cashfree and banking/KYC partners:</strong> payment processing, identity verification, payouts, and refunds</li>
                  <li><strong>Apple and Google:</strong> social sign-in and platform services selected by the user</li>
                  <li><strong>Expo, Apple Push Notification service, and Firebase Cloud Messaging:</strong> delivery of app notifications</li>
                  <li><strong>Hosting, storage, and infrastructure providers:</strong> secure operation of Busiconn</li>
                  <li><strong>Authorities and professional advisers:</strong> when required by law or necessary to protect users and the platform</li>
                </ul>
                <p className="mt-4">Service providers are required to protect personal data and use it only for the services they provide to Busiconn.</p>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Data Retention & Deletion
              </h2>
              <div className="border border-neutral-200 rounded-md p-6">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account data is retained while the account is active.</li>
                  <li>Content, communications, reports, and security records are retained only as reasonably needed for platform operation, safety, disputes, and legal obligations.</li>
                  <li>Financial, KYC, tax, payout, and dispute records may be retained for the period required by applicable law, including up to 7 years where required.</li>
                  <li>Backups may retain deleted data temporarily until they are securely overwritten.</li>
                </ul>
                <p className="mt-4">
                  Delete an account in the mobile app from Profile → Edit Profile → Delete Account,
                  or use our{" "}<a className="text-primary-600 underline" href="/data-deletion">Data Deletion page</a>.
                </p>
              </div>
            </section>

            {/* SECTION */}
            <section className="mt-12">
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Your Rights
              </h2>

              <div className="border border-neutral-200 rounded-md p-6">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access and update your personal data</li>
                  <li>Request access to or correction of your personal data</li>
                  <li>Request account and associated-data deletion, subject to legal retention</li>
                  <li>Opt out of marketing communications</li>
                  <li>Contact us to withdraw consent where processing is based on consent</li>
                </ul>
              </div>
            </section>

            {/* SECTION */}
            <section className="mt-12">
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Data Security
              </h2>

              <div className="border border-neutral-200 rounded-md p-6">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption in transit and access controls for production systems</li>
                  <li>Restricted handling of payment, banking, and KYC data</li>
                  <li>Security monitoring, fraud detection, and operational safeguards</li>
                </ul>
              </div>
            </section>

            {/* SECTION */}
            <section className="mt-12">
              <h2 className="text-xl font-semibold text-neutral-900 mb-4">
                Contact Us
              </h2>

              <div className="border border-neutral-200 rounded-md p-6 text-sm">
                <p>Email: <strong>contact@busiconn.com</strong></p>
                <p>Privacy: <strong>privacy@busiconn.com</strong></p>
              </div>
            </section>

            <p className="text-xs text-neutral-500 text-center mt-16">
              Last Updated: 19 September 2026
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
